import { Button } from '@react-spectrum/s2';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { EmailGeneratingInterstitial } from '@/components/artifact/EmailGeneratingInterstitial';
import { EmailPersonalization } from '@/components/artifact/EmailPersonalization';
import { EmailTemplateSelection } from '@/components/artifact/EmailTemplateSelection';
import { getEmailPanelTitle } from '@/components/artifact/panelMeta';
import { TestSendEmailPopover } from '@/components/artifact/TestSendEmailPopover';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { trackEvent } from '@/utils/fullstory';

import type { EmailHtml } from '../../../../../bridge/types/artifact.ts';
import { EMAIL_STEPS } from '../../../../../bridge/types/setup-steps.ts';
import { isSingleSendableEmailProcess } from '../../../../../bridge/utils/setupCardItems.ts';

interface EmailArtifactPanelProps {
	artifact: EmailHtml;
	stepIndex: number;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const EmailArtifactPanel = ({
	artifact,
	stepIndex,
	onBack,
	onClose,
	style,
}: EmailArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextItem = useGoToNextStep();
	const openChatPanel = useOpenChatPanel();
	useEnsureSetupSnapshot(artifact.process_id);
	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const currentChat = currentChatId
		? (bridgeApi.user_state?.chats?.[currentChatId] ?? null)
		: null;
	const isStandaloneEdit =
		currentChat?.linked_item?.type === 'artifact' &&
		currentChat.linked_item.id === artifact.id;
	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	// A one-off email send (a standalone edit, or a campaign whose only sendable
	// piece is this email) gets the "Send now" path through recipients ->
	// schedule. A genuine multi-piece campaign keeps "Approve": its pieces go out
	// together at campaign activation, not one email at a time. When the snapshot
	// hasn't hydrated yet, fall back to the multi-piece behavior so a campaign is
	// never mislabeled as a single send.
	const isSingleEmailSend =
		isStandaloneEdit ||
		(snapshot != null && isSingleSendableEmailProcess(snapshot));
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Email confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});
	const clampedStepIndex = Math.min(
		Math.max(stepIndex ?? 0, 0),
		EMAIL_STEPS.length - 1,
	);
	const currentStep = EMAIL_STEPS[clampedStepIndex];
	const isFirstStep = clampedStepIndex === 0;
	const isLastStep = clampedStepIndex === EMAIL_STEPS.length - 1;

	const hasInstance = artifact.content.instance != null;
	const selectedTemplateId = artifact.content.selected_template_id ?? null;
	const lastGenerationError = artifact.content.last_generation_error ?? null;

	const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(
		selectedTemplateId,
	);
	const [isRegenerating, setIsRegenerating] = useState(false);

	const isGenerating =
		(!!selectedTemplateId && !hasInstance && !lastGenerationError) ||
		isRegenerating;

	const handleContentChange = useCallback(
		(updates: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: updates,
			});
		},
		[bridgeApi, artifact.id],
	);

	const handleSubmit = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Email Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		if (isSingleEmailSend) {
			openChatPanel({
				panel_type: 'process_recipients',
				item_id: artifact.process_id,
			});
			return;
		}
		markSubmitted();
	}, [
		bridgeApi,
		openChatPanel,
		artifact.id,
		artifact.process_id,
		isSingleEmailSend,
		markSubmitted,
	]);

	const handleGenerate = useCallback(() => {
		if (!pendingTemplateId) return;
		setIsRegenerating(true);
		bridgeApi.sendAction({
			type: 'do_generate_email_for_template',
			artifact_id: artifact.id,
			template_id: pendingTemplateId,
		});
	}, [bridgeApi, artifact.id, pendingTemplateId]);

	const handleChooseDifferentTemplate = useCallback(() => {
		setPendingTemplateId(null);
		setIsRegenerating(false);
		bridgeApi.sendAction({
			type: 'do_edit_artifact',
			artifact_id: artifact.id,
			edits: {
				selected_template_id: null,
				last_generation_error: null,
			},
		});
	}, [bridgeApi, artifact.id]);

	const hasAdvancedOnMount = useRef(false);
	useEffect(() => {
		if (hasAdvancedOnMount.current) return;
		if (
			currentStep === 'EmailTemplateSelection' &&
			hasInstance &&
			!isRegenerating &&
			!lastGenerationError
		) {
			hasAdvancedOnMount.current = true;
			bridgeApi.sendAction({ type: 'do_panel_step_next' });
		}
	}, [
		hasInstance,
		currentStep,
		isRegenerating,
		lastGenerationError,
		bridgeApi,
	]);

	useEffect(() => {
		if (!isRegenerating) return;
		if (hasInstance || lastGenerationError) {
			setIsRegenerating(false);
		}
	}, [hasInstance, lastGenerationError, isRegenerating]);

	const title = useMemo(
		() => getEmailPanelTitle(currentStep, isGenerating),
		[currentStep, isGenerating],
	);
	const showSubmitButton = isLastStep;
	const showTestSendButton = isLastStep && hasInstance;
	const showBackButton = !isFirstStep && !isGenerating;
	const showContinueButton =
		currentStep === 'EmailTemplateSelection' &&
		!isGenerating &&
		!lastGenerationError;
	const continueDisabled =
		!pendingTemplateId || pendingTemplateId === selectedTemplateId;
	const showNextButton =
		!isLastStep && !isGenerating && hasInstance && !showContinueButton;
	const showFooterActions =
		showBackButton ||
		showNextButton ||
		showContinueButton ||
		showSubmitButton ||
		showTestSendButton;

	const actionRenderer = showFooterActions
		? () => (
				<div className="ml-auto flex items-center gap-2">
					{showBackButton && (
						<Button
							type="button"
							variant="secondary"
							onPress={() =>
								bridgeApi.sendAction({ type: 'do_panel_step_back' })
							}
						>
							Back
						</Button>
					)}
					{showContinueButton && (
						<Button
							type="button"
							variant="accent"
							isDisabled={continueDisabled}
							onPress={handleGenerate}
						>
							Continue
						</Button>
					)}
					{showNextButton && (
						<Button
							type="button"
							variant="accent"
							onPress={() =>
								bridgeApi.sendAction({ type: 'do_panel_step_next' })
							}
						>
							Next
						</Button>
					)}
					{showTestSendButton && (
						<TestSendEmailPopover
							instanceId={artifact.content.instance?.cosmos_id ?? ''}
							subject={artifact.content.subject ?? ''}
						/>
					)}
					{showSubmitButton && (
						<Button type="button" variant="accent" onPress={handleSubmit}>
							{isSingleEmailSend && hasInstance ? 'Send now' : 'Approve'}
						</Button>
					)}
				</div>
			)
		: undefined;

	const content = useMemo(() => {
		if (lastGenerationError && currentStep === 'EmailTemplateSelection') {
			return (
				<div
					className="flex min-h-[320px] flex-col items-center justify-center
						gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center"
				>
					<p className="text-base font-medium text-red-700">
						We couldn&apos;t generate this email.
					</p>
					<p className="text-sm text-red-600">
						Try a different template or try again.
					</p>
					<Button
						type="button"
						variant="secondary"
						onPress={handleChooseDifferentTemplate}
					>
						Choose a different template
					</Button>
				</div>
			);
		}

		switch (currentStep) {
			case 'EmailTemplateSelection':
				if (isGenerating) {
					return (
						<EmailGeneratingInterstitial
							selectedTemplateId={pendingTemplateId ?? selectedTemplateId}
						/>
					);
				}
				return (
					<EmailTemplateSelection
						content={artifact.content}
						onContentChange={handleContentChange}
						pendingTemplateId={pendingTemplateId}
						onPendingChange={setPendingTemplateId}
					/>
				);
			case 'EmailPersonalization':
			default:
				return (
					<EmailPersonalization
						content={artifact.content}
						onContentChange={handleContentChange}
					/>
				);
		}
	}, [
		artifact.content,
		currentStep,
		handleChooseDifferentTemplate,
		handleContentChange,
		isGenerating,
		lastGenerationError,
		pendingTemplateId,
		selectedTemplateId,
	]);

	return (
		<ChatPanelLayout
			title={title}
			actionRenderer={submittedView ? undefined : actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{submittedView ?? (
				<div className="flex min-h-0 flex-1 flex-col gap-3">{content}</div>
			)}
		</ChatPanelLayout>
	);
};
